import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {useDisclosure} from '@mantine/hooks';
import {useState} from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {SidebarLinkClients} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {SiteTable} from 'src/pages/Modules/Clients/shared/SiteTable';
import {
  ClientDocumentTitle,
  ClientFormMeta,
} from 'src/pages/Modules/Clients/shared/clientHelpers';
import {ContactsViewTable} from 'src/pages/Modules/Clients/shared/ContactsFields';
import {EntityActiveStatusPanel} from 'src/pages/Modules/Clients/shared/EntityActiveStatusPanel';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {
  useDeleteClientMutation,
  useGetClientQuery,
  useUpdateClientActiveStatusMutation,
} from 'src/redux/queries/client';
import {useClientPermissions} from 'src/shared/auth/permissions/useClientPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2} from 'src/types/FormV2';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const ClientViewPage = () => {
  const navigate = useAppNavigate();
  const {clientId} = useParams();
  const {setAlert} = useAlert();
  const [isDeleting, setIsDeleting] = useState(false);
  const clientPerms = useClientPermissions();
  useSetDocumentTitle(ClientDocumentTitle);

  const validClientId =
    clientId !== undefined && !isNaN(+clientId) ? +clientId : undefined;

  const {data: viewItem, isLoading} = useGetClientQuery(validClientId!, {
    skip: !validClientId,
  });

  const [deleteClient] = useDeleteClientMutation();
  const [updateClientActiveStatus, {isLoading: isTogglingActive}] =
    useUpdateClientActiveStatusMutation();

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!clientId) {
        setAlert('ID not found.', 'error');
        return;
      }

      setIsDeleting(true);

      await deleteClient(+clientId).unwrap();

      setAlert('Client was successfully deleted.', 'success');

      navigate(PathUtility.Clients('list'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsDeleting(false);
  };

  if (isLoading) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Clients}>
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </ContentWrapper>
    );
  }

  if (!viewItem) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.Clients}>
        <Alert color="red" title="Not Found">
          Client not found.
        </Alert>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Clients}>
      <Stack gap="lg">
        <BackBar
          fallbackTo={PathUtility.Clients('list')}
          fallbackLabel="Back to Clients"
          breadcrumbs={[
            {label: 'Clients', to: PathUtility.Clients('list')},
            {label: viewItem.name ?? 'Client'},
          ]}
        />

        <PageHeader>
          <PageHeader.Title icon={SidebarLinkClients.icon}>
            {viewItem.name ?? 'Client'}
          </PageHeader.Title>

          <PageHeader.Badges>
            <PageHeader.Badge
              color={viewItem.active ? 'green' : 'yellow'}
              variant="light">
              {viewItem.active ? 'Active' : 'Inactive'}
            </PageHeader.Badge>
          </PageHeader.Badges>

          <PageHeader.Actions>
            {clientPerms.update && (
              <PageHeader.Action
                variant="edit"
                onClick={() => navigate(PathUtility.ClientsEdit(clientId!))}>
                Edit
              </PageHeader.Action>
            )}
            {clientPerms.delete && (
              <PageHeader.Action
                variant="delete"
                onClick={() => confirmDeleteHandlers.open()}
                loading={isDeleting}>
                Delete
              </PageHeader.Action>
            )}
          </PageHeader.Actions>
        </PageHeader>

        <FormViewComponentV2
          formData={viewItem as IFormDataV2}
          formMeta={ClientFormMeta}
        />

        <ContactsViewTable contacts={viewItem.contacts} />

        <SectionCard title="Sites">
          <SiteTable
            clientId={viewItem.id}
            identifier={`site-for-client-${viewItem.id}`}
            showHeader={false}
          />
        </SectionCard>

        {clientPerms.update && (
          <EntityActiveStatusPanel
            entityLabel="Client"
            isActive={!!viewItem.active}
            loading={isTogglingActive}
            onToggle={() =>
              updateClientActiveStatus({
                id: viewItem.id,
                active: !viewItem.active,
              }).unwrap()
            }
          />
        )}
      </Stack>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this client?"
        body="This action cannot be undone. Any sites associated with this client will be unassociated, not deleted."
      />
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.ClientsView(':clientId'),
  element: <ClientViewPage />,
  module: ModuleEnum.Clients,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
